import { sql } from 'drizzle-orm';
import {
  pgTable,
  index,
  jsonb,
  timestamp,
  varchar,
  text,
  integer,
  decimal,
  boolean,
  uuid,
  primaryKey
} from "drizzle-orm/pg-core";
import { claims, intakeDecisions } from './claims';
import { documents } from './documents';
import { appraisalCarrierResponseEnum, appraisalDocumentTypeEnum, appraisalRoleEnum, appraisalStatusEnum, appraisalWorkflowStatusEnum } from './enums';
import { organizations } from './organizations';
import { tasks } from './tasks';
import { users } from './users';

// External Appraisers - Contact database for external appraisers (used when MPLC is PA)
export const externalAppraisers = pgTable("external_appraisers", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  firstName: varchar("first_name").notNull(),
  lastName: varchar("last_name").notNull(),
  email: varchar("email"),
  phone: varchar("phone"),
  company: varchar("company"),
  address: text("address"),
  city: varchar("city"),
  state: varchar("state"),
  zip: varchar("zip"),
  
  // Appraiser qualifications
  licenseNumber: varchar("license_number"),
  certifications: jsonb("certifications"), // Array of certification details
  specializations: jsonb("specializations"), // Array of specialization areas (residential, commercial, wind, fire, etc.)
  yearsExperience: integer("years_experience"),
  
  // Performance tracking
  casesHandled: integer("cases_handled").default(0),
  averageAwardAmount: decimal("average_award_amount", { precision: 12, scale: 2 }),
  successRate: decimal("success_rate", { precision: 5, scale: 2 }), // Percentage
  preferredByPolicyholders: boolean("preferred_by_policyholders").default(false),
  
  // Availability
  isActive: boolean("is_active").default(true),
  availableStates: jsonb("available_states"), // Array of state codes where they can serve
  
  notes: text("notes"),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_external_appraisers_state").on(table.state),
  index("idx_external_appraisers_is_active").on(table.isActive),
  index("idx_external_appraisers_company").on(table.company),
]);


// Appraisal Assignments - Track appraiser assignments for both PA and Appraisal files
export const appraisalAssignments = pgTable("appraisal_assignments", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  
  // Assignment type and role
  appraisalRole: appraisalRoleEnum("appraisal_role").notNull(), // policyholder_appraiser, carrier_appraiser, umpire
  appraisalStatus: appraisalStatusEnum("appraisal_status").notNull().default("pending"),
  
  // Appraiser can be either internal (MPLC staff) or external
  isInternalAppraiser: boolean("is_internal_appraiser").default(false), // false = external, true = MPLC staff
  internalAppraiserId: varchar("internal_appraiser_id").references(() => users.id), // If MPLC staff
  externalAppraiserId: uuid("external_appraiser_id").references(() => externalAppraisers.id), // If external appraiser
  
  // Assignment details
  assignedAt: timestamp("assigned_at").defaultNow(),
  assignedBy: varchar("assigned_by").references(() => users.id).notNull(),
  acceptedAt: timestamp("accepted_at"),
  
  // Appraisal process tracking
  inspectionDate: timestamp("inspection_date"),
  inspectionCompleted: boolean("inspection_completed").default(false),
  reportSubmittedDate: timestamp("report_submitted_date"),
  awardAmount: decimal("award_amount", { precision: 12, scale: 2 }),
  awardIssuedDate: timestamp("award_issued_date"),
  
  // Umpire selection (if this is umpire role)
  umpireSelectedBy: varchar("umpire_selected_by"), // 'both_appraisers', 'court', 'agreement'
  
  // Notes and documentation
  assignmentNotes: text("assignment_notes"),
  reportFileId: uuid("report_file_id").references(() => documents.id), // Link to appraisal report document
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_appraisal_assignments_claim_id").on(table.claimId),
  index("idx_appraisal_assignments_internal_appraiser_id").on(table.internalAppraiserId),
  index("idx_appraisal_assignments_external_appraiser_id").on(table.externalAppraiserId),
  index("idx_appraisal_assignments_appraisal_role").on(table.appraisalRole),
  index("idx_appraisal_assignments_appraisal_status").on(table.appraisalStatus),
  index("idx_appraisal_assignments_is_internal").on(table.isInternalAppraiser),
]);


// Appraisal Workflow Management Tables

// Appraisal Decisions - Main workflow table
export const appraisalDecisions = pgTable("appraisal_decisions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  intakeDecisionId: uuid("intake_decision_id").references(() => intakeDecisions.id),
  
  // Workflow Status
  status: appraisalWorkflowStatusEnum("status").notNull().default("draft_awaiting_review"),
  
  // AI-Generated Communications
  phExplanationEmailDraft: text("ph_explanation_email_draft"), // AI-drafted email to PH
  phExplanationEmailSubject: varchar("ph_explanation_email_subject"),
  demandLetterDraft: text("demand_letter_draft"), // AI-drafted demand letter
  agreementDraft: text("agreement_draft"), // AI-drafted MPLC-PH agreement
  
  // Content Metadata
  decisionExplanation: text("decision_explanation"), // Why appraisal was recommended
  nextSteps: jsonb("next_steps"), // Array of next steps for PH
  feeStructure: jsonb("fee_structure"), // Fee details {depositAmount, contingencyRate, hourlyRate, etc.}
  timeline: jsonb("timeline"), // Expected timeline milestones
  faqs: jsonb("faqs"), // Frequently asked questions with answers
  
  // Human Review
  reviewedBy: varchar("reviewed_by").references(() => users.id),
  reviewedAt: timestamp("reviewed_at"),
  reviewNotes: text("review_notes"),
  humanApproved: boolean("human_approved").default(false),
  approvedAt: timestamp("approved_at"),
  
  // Communication Tracking
  emailSentAt: timestamp("email_sent_at"),
  emailDeliveredAt: timestamp("email_delivered_at"),
  emailOpenedAt: timestamp("email_opened_at"),
  
  // Task Automation
  callTaskCreated: boolean("call_task_created").default(false),
  callTaskId: uuid("call_task_id").references(() => tasks.id),
  followUpScheduled: boolean("follow_up_scheduled").default(false),
  
  // Metadata
  createdBy: varchar("created_by").references(() => users.id),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_appraisal_decisions_claim_id").on(table.claimId),
  index("idx_appraisal_decisions_status").on(table.status),
  index("idx_appraisal_decisions_intake_id").on(table.intakeDecisionId),
  index("idx_appraisal_decisions_human_approved").on(table.humanApproved),
]);


// Appraisal Agreements - Track PH agreement status
export const appraisalAgreements = pgTable("appraisal_agreements", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  appraisalDecisionId: uuid("appraisal_decision_id").references(() => appraisalDecisions.id, { onDelete: "cascade" }).notNull(),
  
  // Agreement Status
  status: varchar("status").notNull().default("draft"), // draft, sent, viewed, signed, completed
  documentUrl: varchar("document_url"), // Link to signed agreement
  documentId: uuid("document_id").references(() => documents.id),
  
  // E-Signature Tracking
  sentForSignatureAt: timestamp("sent_for_signature_at"),
  viewedAt: timestamp("viewed_at"),
  signedAt: timestamp("signed_at"),
  signedBy: varchar("signed_by"), // PH name
  signatureIpAddress: varchar("signature_ip_address"),
  
  // Agreement Terms
  depositAmount: decimal("deposit_amount", { precision: 12, scale: 2 }),
  contingencyRate: decimal("contingency_rate", { precision: 5, scale: 2 }), // Percentage
  hourlyRate: decimal("hourly_rate", { precision: 8, scale: 2 }),
  flatFee: decimal("flat_fee", { precision: 12, scale: 2 }),
  feeStructureNotes: text("fee_structure_notes"),
  
  // File Upload
  uploadedToClaimFile: boolean("uploaded_to_claim_file").default(false),
  uploadedAt: timestamp("uploaded_at"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_appraisal_agreements_claim_id").on(table.claimId),
  index("idx_appraisal_agreements_decision_id").on(table.appraisalDecisionId),
  index("idx_appraisal_agreements_status").on(table.status),
  index("idx_appraisal_agreements_signed_at").on(table.signedAt),
]);


// Appraisal Tracking - Track carrier response and appraiser assignments
export const appraisalTracking = pgTable("appraisal_tracking", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  appraisalDecisionId: uuid("appraisal_decision_id").references(() => appraisalDecisions.id, { onDelete: "cascade" }).notNull(),
  
  // Demand Letter Tracking
  demandLetterSentAt: timestamp("demand_letter_sent_at"),
  demandLetterDeadline: timestamp("demand_letter_deadline"), // Deadline for carrier to respond
  demandDisputedAmounts: jsonb("demand_disputed_amounts"), // List of disputed line items with amounts
  
  // Carrier Response
  carrierResponse: appraisalCarrierResponseEnum("carrier_response").default("pending"),
  carrierRespondedAt: timestamp("carrier_responded_at"),
  carrierResponseNotes: text("carrier_response_notes"),
  
  // Carrier Appraiser Details
  carrierAppraiserName: varchar("carrier_appraiser_name"),
  carrierAppraiserEmail: varchar("carrier_appraiser_email"),
  carrierAppraiserPhone: varchar("carrier_appraiser_phone"),
  carrierAppraiserCompany: varchar("carrier_appraiser_company"),
  carrierAppraiserAssignedAt: timestamp("carrier_appraiser_assigned_at"),
  
  // MPLC Appraiser Details
  mplcAppraiserName: varchar("mplc_appraiser_name"),
  mplcAppraiserEmail: varchar("mplc_appraiser_email"),
  mplcAppraiserPhone: varchar("mplc_appraiser_phone"),
  mplcAppraiserAssignedAt: timestamp("mplc_appraiser_assigned_at"),
  appraiserContactEmailSent: boolean("appraiser_contact_email_sent").default(false),
  
  // Umpire Details
  umpireName: varchar("umpire_name"),
  umpireEmail: varchar("umpire_email"),
  umpirePhone: varchar("umpire_phone"),
  umpireCompany: varchar("umpire_company"),
  umpireAssignedAt: timestamp("umpire_assigned_at"),
  
  // Deadline Tracking & Reminders
  remindersSent: jsonb("reminders_sent"), // Array of {sentAt, type, daysSince}
  nextReminderAt: timestamp("next_reminder_at"),
  isOverdue: boolean("is_overdue").default(false),
  
  // State-Specific Rules
  isRestrictedScopeState: boolean("is_restricted_scope_state").default(false),
  stateRuleHint: text("state_rule_hint"), // State-specific guidance
  
  // Alternative Options (if carrier rejects)
  valueOnlyResubmissionAvailable: boolean("value_only_resubmission_available").default(false),
  openChairAppraisalAvailable: boolean("open_chair_appraisal_available").default(false),
  openChairAttorneyCostEstimate: varchar("open_chair_attorney_cost_estimate"), // e.g., "$2,500-$5,000"
  litigationOptionProvided: boolean("litigation_option_provided").default(false),
  
  // Education & Routing (after rejection)
  educationCallScheduled: boolean("education_call_scheduled").default(false),
  educationCallCompletedAt: timestamp("education_call_completed_at"),
  chosenOption: varchar("chosen_option"), // value_only, open_chair, litigation, out_of_pocket, none

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_appraisal_tracking_claim_id").on(table.claimId),
  index("idx_appraisal_tracking_decision_id").on(table.appraisalDecisionId),
  index("idx_appraisal_tracking_carrier_response").on(table.carrierResponse),
  index("idx_appraisal_tracking_deadline").on(table.demandLetterDeadline),
  index("idx_appraisal_tracking_is_overdue").on(table.isOverdue),
]);


// Appraisal Workflow Documents - Store generated documents
export const appraisalWorkflowDocuments = pgTable("appraisal_workflow_documents", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  appraisalDecisionId: uuid("appraisal_decision_id").references(() => appraisalDecisions.id, { onDelete: "cascade" }).notNull(),
  
  // Document Details
  documentType: appraisalDocumentTypeEnum("document_type").notNull(),
  title: varchar("title").notNull(),
  content: text("content").notNull(), // HTML or plain text content
  
  // Generation Metadata
  generatedBy: varchar("generated_by").default("ai"), // 'ai' or user ID
  templateUsed: varchar("template_used"),
  tokens: jsonb("tokens"), // Template tokens used for generation
  
  // Status
  status: varchar("status").default("draft"), // draft, approved, sent, delivered
  approvedBy: varchar("approved_by").references(() => users.id),
  approvedAt: timestamp("approved_at"),
  sentAt: timestamp("sent_at"),
  
  // Recipients
  recipientEmail: varchar("recipient_email"),
  recipientName: varchar("recipient_name"),
  ccEmails: jsonb("cc_emails"), // Array of CC email addresses
  
  // File Storage
  fileUrl: varchar("file_url"), // URL to PDF or stored file
  fileFormat: varchar("file_format").default("pdf"), // pdf, docx, html
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_appraisal_workflow_docs_claim_id").on(table.claimId),
  index("idx_appraisal_workflow_docs_decision_id").on(table.appraisalDecisionId),
  index("idx_appraisal_workflow_docs_type").on(table.documentType),
  index("idx_appraisal_workflow_docs_status").on(table.status),
]);
